import './Homepage.css';
export default function HomePage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header with Main Image */}
      <header className="header relative h-96 overflow-hidden">
        <div className="absolute inset-0 header"></div>
      </header>

      {/* Bio Section */}
      <section className="max-w-3xl mx-auto px-6 py-16">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-6">Why I Am Writing the Newsletter</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
            I am not writing from authority. I am writing from presence.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
            I am not positioning myself as someone who figured
            everything out and is now teaching others how to do the
            same. I am standing in the middle with them and naming
            what that middle actually feels like.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
            This work respects the reader’s intelligence and lived
            experience. It does not rush them out of discomfort or push
            them toward a forced resolution. It acknowledges that
            uncertainty is real, that loss can be quiet and disorienting,
            and that strength does not always look impressive from the
            outside.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
            Integrity is at the center of this writing. I refuse to skip
            steps, both in my own story and in how I invite others into
            it. This is a pushback against performative resilience. It
            makes space for a slower, more honest kind of rebuilding.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
            This is not content created for clicks or quick validation. It
            is a place. A steady voice people can return to when
            everything else feels transactional or rushed. I am not
            trying to fix people or sell them a transformation. I am
            offering language, clarity, and companionship in a season
            most people feel alone in.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
            At its core, this work exists to say: You are not behind. You
            are not broken. You are human.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
            This is why I am writing the newsletter and the books. This
            is the work.
          </p>
        </div>
      </section>

      {/* External Links Section */}
      <section className="max-w-3xl mx-auto px-6 pb-16">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-6">Find Me Online</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <a 
              href="https://www.amazon.com/dp/B0GKCPJCX2" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-4 bg-gradient-to-r from-orange-400 to-orange-500 text-white rounded-lg hover:from-orange-500 hover:to-orange-600 transition-all duration-200 shadow-md hover:shadow-lg"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M14.12 14.12C14.02 14.27 13.9 14.4 13.76 14.5C13.53 14.69 13.23 14.8 12.91 14.8C12.28 14.8 11.78 14.3 11.78 13.67C11.78 13.04 12.28 12.54 12.91 12.54C13.23 12.54 13.53 12.65 13.76 12.84C13.9 12.94 14.02 13.07 14.12 13.22L16.27 12.54C15.97 11.35 14.93 10.46 13.67 10.33V8.89H11.89V10.33C10.33 10.51 9.11 11.85 9.11 13.49C9.11 15.13 10.33 16.47 11.89 16.65V21H13.67V16.65C15.23 16.47 16.45 15.13 16.45 13.49C16.45 13.36 16.44 13.23 16.41 13.1L14.12 14.12ZM20.64 15.85L21.6 16.43C21.78 16.55 21.85 16.8 21.73 17C20.68 18.8 18.76 20 16.5 20C12.91 20 10 17.09 10 13.5C10 9.91 12.91 7 16.5 7C18.76 7 20.68 8.2 21.73 10C21.85 10.2 21.78 10.45 21.6 10.57L20.64 11.15C20.5 11.24 20.31 11.21 20.19 11.07C19.39 9.99 18.03 9.3 16.5 9.3C14.18 9.3 12.3 11.18 12.3 13.5C12.3 15.82 14.18 17.7 16.5 17.7C18.03 17.7 19.39 17.01 20.19 15.93C20.31 15.79 20.5 15.76 20.64 15.85Z"/>
              </svg>
              <span className="font-semibold">Amazon</span>
            </a>
            
            <a 
                          href="https://www.linkedin.com/build-relation/newsletter-follow?entityUrn=7393511990545039360" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-md hover:shadow-lg"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z"/>
              </svg>
              <span className="font-semibold">LinkedIn Newsletter</span>
            </a>
            
            <a 
              href="https://x.com/5kindsofsmoke" 
              target="_blank" 
              rel="noopener noreferrer"
                          className="flex items-center gap-3 p-4 bg-gradient-to-r from-gray-700 to-gray-800 text-white rounded-lg hover:from-sky-500 hover:to-sky-600 transition-all duration-200 shadow-md hover:shadow-lg"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M22.46 6c-.85.38-1.78.64-2.75.76a4.8 4.8 0 0 0 2.1-2.66c-.93.55-1.95.95-3.04 1.17a4.77 4.77 0 0 0-8.13 4.35A13.54 13.54 0 0 1 3.8 4.67a4.77 4.77 0 0 0 1.48 6.37 4.73 4.73 0 0 1-2.16-.6v.06c0 2.31 1.64 4.24 3.82 4.68a4.76 4.76 0 0 1-2.15.08 4.78 4.78 0 0 0 4.46 3.31A9.57 9.57 0 0 1 2 20.13a13.5 13.5 0 0 0 7.29 2.14c8.75 0 13.54-7.25 13.54-13.54 0-.21 0-.41-.01-.62A9.68 9.68 0 0 0 25 5.79a9.47 9.47 0 0 1-2.54.7z"/>
              </svg>
              <span className="font-semibold">X (Twitter)</span>
            </a>

            <a
                href="https://www.facebook.com/5.kinds.of.smoke"
                target="_blank"
                rel="noopener noreferrer"
                          className="flex items-center gap-3 p-4 bg-gradient-to-r from-sky-400 to-sky-500  text-white rounded-lg hover:from-gray-800 hover:to-gray-900 transition-all duration-200 shadow-md hover:shadow-lg"
            >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2A10 10 0 0 0 2 12c0 4.42 2.87 8.17 6.84 9.5.5.08.66-.23.66-.5v-1.69c-2.77.6-3.36-1.34-3.36-1.34-.46-1.16-1.11-1.47-1.11-1.47-.91-.62.07-.6.07-.6 1 .07 1.53 1.03 1.53 1.03.87 1.52 2.34 1.07 2.91.83.09-.65.35-1.09.63-1.34-2.22-.25-4.55-1.11-4.55-4.92 0-1.11.38-2 1.03-2.71-.1-.25-.45-1.29.1-2.64 0 0 .84-.27 2.75 1.02.79-.22 1.65-.33 2.5-.33.85 0 1.71.11 2.5.33 1.91-1.29 2.75-1.02 2.75-1.02.55 1.35.2 2.39.1 2.64.65.71 1.03 1.6 1.03 2.71 0 3.82-2.34 4.66-4.57 4.91.36.31.69.92.69 1.85V21c0 .27.16.59.67.5C19.14 20.16 22 16.42 22 12A10 10 0 0 0 12 2z" />
                </svg>
                <span className="font-semibold">Facebook</span>
            </a>

            <a 
              href="/doc/five-kinds-of-smoke-press-release.pdf" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-4 bg-gradient-to-r from-green-700 to-green-800 text-white rounded-lg hover:from-gray-800 hover:to-gray-900 transition-all duration-200 shadow-md hover:shadow-lg"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2A10 10 0 0 0 2 12c0 4.42 2.87 8.17 6.84 9.5.5.08.66-.23.66-.5v-1.69c-2.77.6-3.36-1.34-3.36-1.34-.46-1.16-1.11-1.47-1.11-1.47-.91-.62.07-.6.07-.6 1 .07 1.53 1.03 1.53 1.03.87 1.52 2.34 1.07 2.91.83.09-.65.35-1.09.63-1.34-2.22-.25-4.55-1.11-4.55-4.92 0-1.11.38-2 1.03-2.71-.1-.25-.45-1.29.1-2.64 0 0 .84-.27 2.75 1.02.79-.22 1.65-.33 2.5-.33.85 0 1.71.11 2.5.33 1.91-1.29 2.75-1.02 2.75-1.02.55 1.35.2 2.39.1 2.64.65.71 1.03 1.6 1.03 2.71 0 3.82-2.34 4.66-4.57 4.91.36.31.69.92.69 1.85V21c0 .27.16.59.67.5C19.14 20.16 22 16.42 22 12A10 10 0 0 0 12 2z"/>
              </svg>
              <span className="font-semibold">Press Release</span>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-6 text-center">
        <p className="text-sm">© 2024 All Rights Reserved</p>
      </footer>
    </div>
  );
}